import Axios from "axios";
import { EventEmitter } from "./event.service";
import { getLoginToken, clearLoginToken, setLoginToken } from "./storage";
import { firebase_auth } from "./firebase-config";
import { shouldSuppressForcedLogout } from "../utils/sessionDeviceInfo";
import { logAuthSession } from "../utils/authSessionDebug";
const axiosInstance = Axios.create();

// Axios instance for non-critical requests (e.g. Stripe quantity sync) — never triggers logout on error
const axiosNoLogout = Axios.create();
function isCloudFunctionsRequest(url) {
  if (typeof url !== "string") return false;
  return (
    url.includes("cloudfunctions.net") ||
    url.includes("/cloudfunctions-stage/") ||
    url.includes("/cloudfunctions-dev/") ||
    url.includes("/cloudfunctions-prod/")
  );
}

function isSendEmailDataRequest(url) {
  return typeof url === "string" && url.includes("sendEmailData");
}

function resolveCompanyIdForPayload() {
  if (typeof window === "undefined") return "";
  return (
    localStorage.getItem("companyId") ||
    localStorage.getItem("userId") ||
    ""
  );
}

function injectCompanyIdForEmailRequest(config) {
  if (!isSendEmailDataRequest(config?.url)) return config;
  const payload = config?.data;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return config;
  }
  if (payload.companyId || payload.companyID) return config;

  const resolvedCompanyId = resolveCompanyIdForPayload();
  if (!resolvedCompanyId) return config;

  config.data = {
    ...payload,
    companyId: resolvedCompanyId,
  };
  return config;
}

async function getFreshAuthToken() {
  const currentUser = firebase_auth?.currentUser;
  if (currentUser?.getIdToken) {
    try {
      const freshToken = await currentUser.getIdToken();
      if (freshToken) {
        setLoginToken(freshToken);
        return freshToken;
      }
    } catch (error) {
      logAuthSession("token_refresh_failed", {
        message: error?.message,
      });
      console.warn("Failed to refresh Firebase auth token:", error?.message);
    }
  }

  return getLoginToken();
}

axiosNoLogout.interceptors.request.use(
  async (config) => {
    const token = await getFreshAuthToken();
    const isCloudFunction = isCloudFunctionsRequest(config.url);
    if (token && isCloudFunction && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return injectCompanyIdForEmailRequest(config);
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getFreshAuthToken();
    const isCloudFunction = isCloudFunctionsRequest(config.url);
    if (token && isCloudFunction && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return injectCompanyIdForEmailRequest(config);
  },
  (error) => Promise.reject(error)
);

// URLs that must never trigger logout on error (401, 500, etc.)
const SKIP_LOGOUT_URLS = ["stripeProxyUpdateSubscriptionQuantity"];

const errorHandler = (error) => {
  const skipAuthRedirect = error?.config?.skipAuthRedirect === true;
  const requestUrl = error?.config?.url || "";
  const isSkipLogoutUrl = SKIP_LOGOUT_URLS.some((pattern) => requestUrl.includes(pattern));
  if (skipAuthRedirect || isSkipLogoutUrl) return;
  let token = getLoginToken();
  if (token !== "" && error?.response?.status === 401) {
    if (shouldSuppressForcedLogout()) {
      logAuthSession("forced_logout_suppressed", {
        trigger: "axios_401",
        url: requestUrl,
      });
      return;
    }
    logAuthSession("forced_logout", {
      trigger: "axios_401",
      url: requestUrl,
    });
    EventEmitter.dispatch("logoutUser");
    clearLoginToken();
    window.location.href = "/login";
  }
};

axiosInstance.interceptors.response.use(
  (config) => {
    return config;
  },
  async (error) => {
    const requestUrl = error?.config?.url || "";
    const status = error?.response?.status;
    const isEmail403 = isSendEmailDataRequest(requestUrl) && status === 403;
    const alreadyRetried = error?.config?._emailAuthRetried === true;

    if (isEmail403 && !alreadyRetried) {
      try {
        const refreshedToken = firebase_auth?.currentUser?.getIdToken
          ? await firebase_auth.currentUser.getIdToken(true)
          : "";

        if (refreshedToken) {
          setLoginToken(refreshedToken);
          const retryConfig = {
            ...error.config,
            _emailAuthRetried: true,
            headers: {
              ...(error.config?.headers || {}),
              Authorization: `Bearer ${refreshedToken}`,
            },
          };
          return axiosInstance.request(retryConfig);
        }
      } catch (refreshError) {
        console.warn("sendEmailData retry after token refresh failed:", refreshError?.message);
      }
    }

    errorHandler(error);
    return Promise.reject(error);
  }
);

export default axiosInstance;
export { axiosNoLogout };
